import { useEffect, useRef, useState } from 'react';

/**
 * Легкий попап-календар без зовнішніх залежностей. Єдиний компонент вибору дати
 * у проєкті (замість нативних input type=date / datetime-local).
 * - Стрілки ‹ › для місяця, ‹‹ ›› для року (швидке листання).
 * - Клік по дню — вибір; дабл-клік по дню — вибір + закрити.
 * - withTime=true → поле часу HH:MM, value/onChange у форматі YYYY-MM-DDTHH:MM.
 *   Iнакше value/onChange — YYYY-MM-DD.
 */
interface Props {
  value: string;                       // YYYY-MM-DD або YYYY-MM-DDTHH:MM
  onChange: (v: string) => void;
  disabled?: boolean;
  withTime?: boolean;
  style?: React.CSSProperties;
}

const MONTHS = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];
const WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

function pad(n: number): string { return String(n).padStart(2, '0'); }

// Розбити value на date-частину (YYYY-MM-DD) і time-частину (HH:MM).
function splitVal(v: string): { date: string; time: string } {
  if (!v) return { date: '', time: '' };
  const [d, t] = v.split('T');
  return { date: d ?? '', time: (t ?? '').slice(0, 5) };
}
function joinVal(date: string, time: string, withTime: boolean): string {
  if (!date) return '';
  return withTime ? `${date}T${time || '00:00'}` : date;
}
function dateParts(date: string): { y: number; m: number; d: number } | null {
  const p = date ? date.split('-').map(Number) : [];
  if (p.length !== 3 || p.some((n) => Number.isNaN(n))) return null;
  return { y: p[0]!, m: p[1]!, d: p[2]! };
}
function fmtDisplay(v: string, withTime: boolean): string {
  const { date, time } = splitVal(v);
  const dp = dateParts(date);
  if (!dp) return '';
  const base = `${pad(dp.d)}.${pad(dp.m)}.${dp.y}`;
  return withTime && time ? `${base} ${time}` : base;
}

export default function DatePopover({ value, onChange, disabled, withTime = false, style }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const { date: curDate, time: curTime } = splitVal(value);
  const initDp = dateParts(curDate);
  const [viewY, setViewY] = useState(initDp ? initDp.y : new Date().getFullYear());
  const [viewM, setViewM] = useState(initDp ? initDp.m - 1 : new Date().getMonth());

  useEffect(() => {
    const dp = dateParts(splitVal(value).date);
    if (dp) { setViewY(dp.y); setViewM(dp.m - 1); }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
  const firstWd = (new Date(viewY, viewM, 1).getDay() + 6) % 7;
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWd; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  function step(deltaM: number, deltaY: number) {
    let m = viewM + deltaM, y = viewY + deltaY;
    if (m < 0) { m += 12; y -= 1; }
    if (m > 11) { m -= 12; y += 1; }
    setViewM(m); setViewY(y);
  }

  function pick(d: number, close: boolean) {
    onChange(joinVal(`${viewY}-${pad(viewM + 1)}-${pad(d)}`, curTime, withTime));
    if (close) setOpen(false);
  }
  function setTime(t: string) {
    onChange(joinVal(curDate || `${viewY}-${pad(viewM + 1)}-${pad(1)}`, t, withTime));
  }

  const sel = dateParts(curDate);
  const isSel = (d: number) => !!sel && sel.y === viewY && sel.m - 1 === viewM && sel.d === d;
  const isWeekend = (d: number) => (new Date(viewY, viewM, d).getDay() + 6) % 7 >= 5;

  const btn: React.CSSProperties = {
    padding: '0.15rem 0.4rem', border: '1px solid var(--border)',
    borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: '0.85rem',
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        readOnly value={fmtDisplay(value, withTime)} disabled={disabled}
        placeholder={withTime ? 'дд.мм.рррр гг:хх' : 'дд.мм.рррр'}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{ width: '100%', cursor: disabled ? 'default' : 'pointer', ...style }} />
      {open && !disabled && (
        <div style={{
          position: 'absolute', zIndex: 50, top: '100%', left: 0, marginTop: 4,
          background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: '0.6rem', minWidth: 250,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem', gap: '0.2rem' }}>
            <span style={{ display: 'flex', gap: '0.2rem' }}>
              <button type="button" style={btn} title="Попередній рік" onClick={() => step(0, -1)}>‹‹</button>
              <button type="button" style={btn} title="Попередній місяць" onClick={() => step(-1, 0)}>‹</button>
            </span>
            <b style={{ fontSize: '0.85rem' }}>{MONTHS[viewM]} {viewY}</b>
            <span style={{ display: 'flex', gap: '0.2rem' }}>
              <button type="button" style={btn} title="Наступний місяць" onClick={() => step(1, 0)}>›</button>
              <button type="button" style={btn} title="Наступний рік" onClick={() => step(0, 1)}>››</button>
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, textAlign: 'center' }}>
            {WD.map((w) => (
              <div key={w} style={{ fontSize: '0.7rem', color: 'var(--muted)', fontWeight: 600 }}>{w}</div>
            ))}
            {cells.map((d, i) => d === null ? <div key={i} /> : (
              <div key={i}
                onClick={() => pick(d, false)}
                onDoubleClick={() => pick(d, true)}
                title="Клік — обрати; подвійний клік — обрати й закрити"
                style={{
                  padding: '0.3rem 0', borderRadius: 4, cursor: 'pointer',
                  fontSize: '0.8rem', userSelect: 'none',
                  background: isSel(d) ? 'var(--primary)' : 'transparent',
                  color: isSel(d) ? '#fff' : (isWeekend(d) ? '#dc2626' : 'inherit'),
                  fontWeight: isSel(d) ? 700 : 400,
                }}>
                {d}
              </div>
            ))}
          </div>
          {withTime && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>Час:</span>
              <input type="time" value={curTime || '00:00'}
                onChange={(e) => setTime(e.target.value)}
                style={{ flex: 1 }} />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.4rem' }}>
            <button type="button" style={btn} onClick={() => {
              const t = new Date();
              setViewY(t.getFullYear()); setViewM(t.getMonth());
              const ds = `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
              onChange(joinVal(ds, withTime ? `${pad(t.getHours())}:${pad(t.getMinutes())}` : curTime, withTime));
            }}>Сьогодні</button>
            <button type="button" style={btn} onClick={() => setOpen(false)}>Закрити</button>
          </div>
        </div>
      )}
    </div>
  );
}
